import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { questionnaireDetailDtoSchema } from '@/features/questionnaires/schemas/questionnaireSchemas';
import { apiClient } from '@/lib/api/client';
import { publicApiClient } from '@/lib/api/publicClient';
import { useTenantStore } from '@/stores/tenantStore';

import { usePublicRun } from '../publicRun';
import type { SessionStepDto } from '../schemas/sessionSchemas';

/**
 * Questionnaire names for a session's questionnaire steps, keyed by step id.
 *
 * WHY A FETCH RATHER THAN READING THE SESSION. A step's `displayName` is copied from the workflow when
 * the run starts and then frozen — which is right, it is the audit record of what the applicant was
 * shown. But a step created before the builder started naming these steps carries the generic
 * "Questionnaire", and nothing in the session payload can improve on it: the frozen snapshot holds the
 * attached questionnaire's id, and the snapshot is deliberately not exposed to the client.
 *
 * So the name is resolved from the questionnaire itself. `useQueries` because the count is only known
 * at render time — one query per questionnaire step, and it CANNOT be a loop of `useQuery`.
 *
 * The query key and fetcher match `useSessionQuestionnaire` exactly, so these share one cache with the
 * answers view: opening a step costs nothing extra, and the names are already warm when it opens.
 */
export function useSessionQuestionnaireNames(
  sessionId: string | undefined,
  steps: readonly SessionStepDto[],
): Map<string, string> {
  const tenantId = useTenantStore((s) => s.tenantId);
  const publicRun = usePublicRun();
  const token = publicRun?.token;

  const questionnaireSteps = steps.filter((s) => s.stepType === 'questionnaire');
  const enabled = !!token || (!!tenantId && !!sessionId);

  const results = useQueries({
    queries: questionnaireSteps.map((step) => ({
      queryKey: ['session-questionnaire', token ?? tenantId ?? '', sessionId ?? '', step.id],
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const path = token
          ? `api/v1/public/sessions/${token}/steps/${step.id}/questionnaire`
          : `api/v1/t/${tenantId}/sessions/${sessionId}/steps/${step.id}/questionnaire`;
        const client = token ? publicApiClient : apiClient;
        const raw = await client.get(path, { signal }).json();
        return questionnaireDetailDtoSchema.parse(raw);
      },
      enabled,
      // From the run's FROZEN snapshot, so it cannot change for the life of the session.
      staleTime: Number.POSITIVE_INFINITY,
      retry: false,
    })),
  });

  /**
   * Memoised so the returned Map keeps its identity while the data is unchanged.
   *
   * A fresh Map every render would be a new reference every render, which silently defeats any
   * `useMemo` or `useEffect` downstream that depends on it — the welcome overview would rebuild its
   * rows on every keystroke elsewhere on the page.
   *
   * The dependency is the resolved names joined into a string, because that is the only thing that can
   * change the result: `results` itself is a new array each render.
   */
  // JSON rather than a delimiter-joined string: a questionnaire name is free text and may contain
  // whatever character we picked as the separator.
  const signature = JSON.stringify(
    questionnaireSteps.map((step, index) => [step.id, results[index]?.data?.name ?? '']),
  );

  return useMemo(() => {
    const entries = JSON.parse(signature) as Array<[string, string]>;
    const names = new Map<string, string>();
    for (const [id, name] of entries) {
      const trimmed = name.trim();
      if (id && trimmed) names.set(id, trimmed);
    }
    return names;
  }, [signature]);
}
